/**
 * Azure Speech streaming live-transcript (Phase B, WebRTC voice channel only).
 *
 * Runs the Azure Speech SDK in the browser for continuous, low-latency
 * recognition of the USER's speech, purely to drive the displayed user
 * transcript with live word-by-word partials. Enabled per-customer via
 * config.liveTranscriptEngine === 'azure_speech'. The subscription key stays
 * server-side; the browser fetches a short-lived auth token from
 * /api/speech-token.
 *
 * Isolation: this module is imported only by VoiceBotPanel (voice channel).
 * Chat and telephony never touch it.
 */
import { getApiUrl } from '../config/api'
import type { LanguageCode } from '../config/language'

// Pinned to the installed version; loaded on-demand from CDN so the embeddable
// widget.js bundle stays lean for customers who don't use this engine.
const SPEECH_SDK_CDN =
  'https://cdn.jsdelivr.net/npm/microsoft-cognitiveservices-speech-sdk@1.51.0/distrib/browser/microsoft.cognitiveservices.speech.sdk.bundle-min.js'

let sdkLoadPromise: Promise<any> | null = null

function loadSpeechSdk(): Promise<any> {
  const w = window as any
  if (w.SpeechSDK) return Promise.resolve(w.SpeechSDK)
  if (sdkLoadPromise) return sdkLoadPromise
  sdkLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = SPEECH_SDK_CDN
    script.async = true
    script.onload = () => {
      if (w.SpeechSDK) resolve(w.SpeechSDK)
      else reject(new Error('SpeechSDK global missing after load'))
    }
    script.onerror = () => reject(new Error('Failed to load Speech SDK from CDN'))
    document.head.appendChild(script)
  })
  return sdkLoadPromise
}

// Map the widget's language codes to Azure Speech recognition locales.
const SPEECH_LOCALE: Record<LanguageCode, string> = {
  he: 'he-IL',
  en: 'en-US',
  ru: 'ru-RU',
  fr: 'fr-FR',
  ar: 'ar-SA',
}

export interface LiveSpeechHandle {
  stop: () => void
}

export interface StartLiveSpeechOptions {
  publicKey: string | undefined
  language: LanguageCode
  onPartial: (text: string) => void
  onFinal: (text: string) => void
  // Gate: return true while the bot is speaking so we don't transcribe its
  // voice bleeding through the speakers.
  isBotSpeaking: () => boolean
  log?: (message: string) => void
}

async function fetchSpeechToken(
  publicKey: string | undefined
): Promise<{ token: string; region: string } | null> {
  try {
    const res = await fetch(getApiUrl('/api/speech-token'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(publicKey ? { 'X-Iland-Public-Key': publicKey } : {}),
      },
      body: JSON.stringify({ publicKey }),
    })
    if (!res.ok) return null
    const data = await res.json()
    return data.token ? { token: data.token, region: data.region } : null
  } catch {
    return null
  }
}

export async function startLiveSpeechTranscript(
  opts: StartLiveSpeechOptions
): Promise<LiveSpeechHandle | null> {
  const { publicKey, language, onPartial, onFinal, isBotSpeaking, log } = opts
  const first = await fetchSpeechToken(publicKey)
  if (!first) {
    log?.('Live transcript: token unavailable (not enabled or misconfigured)')
    return null
  }

  let SpeechSDK: any
  try {
    SpeechSDK = await loadSpeechSdk()
  } catch (err) {
    log?.(`Live transcript: SDK load failed — ${err instanceof Error ? err.message : String(err)}`)
    return null
  }

  const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(first.token, first.region)
  speechConfig.speechRecognitionLanguage = SPEECH_LOCALE[language] || 'he-IL'
  const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput()
  const recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig)

  recognizer.recognizing = (_s: any, e: any) => {
    if (isBotSpeaking()) return
    const text = e.result?.text || ''
    if (text) onPartial(text)
  }
  recognizer.recognized = (_s: any, e: any) => {
    onPartial('')
    if (e.result?.reason !== SpeechSDK.ResultReason.RecognizedSpeech) return
    const text = (e.result.text || '').trim()
    if (!text) return
    if (isBotSpeaking()) return
    onFinal(text)
  }
  recognizer.canceled = (_s: any, e: any) => {
    log?.(`Live transcript canceled: ${e.reason}${e.errorDetails ? ' — ' + e.errorDetails : ''}`)
  }

  await new Promise<void>((resolve, reject) =>
    recognizer.startContinuousRecognitionAsync(() => resolve(), (err: any) => reject(err))
  )
  log?.('Live transcript streaming (azure_speech)…')

  // Auth token lives ~10 min; refresh it in place every 8 min.
  const refreshTimer = setInterval(async () => {
    const next = await fetchSpeechToken(publicKey)
    if (next) recognizer.authorizationToken = next.token
  }, 8 * 60 * 1000)

  let stopped = false
  return {
    stop: () => {
      if (stopped) return
      stopped = true
      clearInterval(refreshTimer)
      try {
        recognizer.stopContinuousRecognitionAsync(
          () => {
            try {
              recognizer.close()
            } catch {
              /* noop */
            }
          },
          () => {
            try {
              recognizer.close()
            } catch {
              /* noop */
            }
          }
        )
      } catch {
        /* noop */
      }
    },
  }
}
